import { PropsWithChildren, ReactElement } from "react";
import { connect, MapStateToProps, MergeProps } from "react-redux";

import { IReduxEnumType } from "@multiweb/reactCore/enums";
import { createGetLocalizedOptions, getPrimaryText } from "@multiweb/reactCore/i18n";
import { IAppState } from "@multiweb/types/react";
import { ComboBoxOption } from "../../ComboBoxComponent";
import { FAKE_COMPONENT_VALUE_LOC_KEY } from "../../locKeys";
import {
    LocalizedComboBoxOptionsWrapperComponent,
    LocalizedComboBoxOptionsWrapperComponentProps,
} from "./LocalizedComboBoxOptionsWrapperComponent";
import { getEmptyOption, getExtendedLocalizedOptions, getMultiEditOption, getPlaceholderOption, updateOptionsWithHidden } from "./utils";

export type LocalizedComboBoxOptionsWrapperChildrenProps = {
     options: ReadonlyArray<ComboBoxOption>;
};

export type LocalizedComboBoxOptionsWrapperProps = Readonly<PropsWithChildren<{
    /** Typ enumu, ze kterého se budou vytvářet options.
     * Není zde předpoklad, že by se enumType měnil za chodu aplikace, a proto jsou na jeho základě vytvořeny stejné lokalizované optiony.
     * Považujeme ho tedy jako statický.
     */
    enumType: IReduxEnumType;

    /** Lokalizační klíč pro placeholder option */
    placeholderLocKey?: string;

    /** Příznak, zda je aktivní multieditace */
    multiEditActive?: boolean;

    /** Přiznak, zda je aktivní režim editace */
    isEditing?: boolean;

    /** Přiznak, zda je combobox povinný */
    required?: boolean;

    /** Řetězec obsahující položky comboboxu, které se mají skrýt */
    hideValues?: string;

    /** Potomek */
    children: (props: LocalizedComboBoxOptionsWrapperChildrenProps) => ReactElement;
}>>;

type StateProps = {
    /** Lokalizovaný text možnosti pro multieditaci */
    multiEditOptionText?: string;
    /** Lokalizovaný text pro placeholder comboboxu */
    placeholderOptionText?: string;
    /** lokalizované options, které se zkonvertují na základě typu enumu */
    localizedOptions: ReadonlyArray<ComboBoxOption>;
};

const createMapStateToProps: (state: IAppState, props: LocalizedComboBoxOptionsWrapperProps) => MapStateToProps<StateProps, LocalizedComboBoxOptionsWrapperProps, IAppState> = (
    _state,
    { enumType },
) => {
    const getLocalizedOptions = createGetLocalizedOptions(getExtendedLocalizedOptions(enumType));
    return (state, { multiEditActive, placeholderLocKey }) => ({
        multiEditOptionText: multiEditActive ? getPrimaryText(state, FAKE_COMPONENT_VALUE_LOC_KEY) ?? "" : undefined,
        placeholderOptionText: placeholderLocKey ? getPrimaryText(state, placeholderLocKey) ?? "" : undefined,
        localizedOptions: getLocalizedOptions(state),
    });
};

const mergeProps: MergeProps<StateProps, undefined, LocalizedComboBoxOptionsWrapperProps, LocalizedComboBoxOptionsWrapperComponentProps> = (
    { multiEditOptionText, placeholderOptionText, localizedOptions },
    _dispatchProps,
    { hideValues, isEditing, required, children },
) => {
    const placeholderOption = getPlaceholderOption(placeholderOptionText);
    const emptyOption = getEmptyOption(isEditing, required);
    const localizedOptionsWithHidden = updateOptionsWithHidden(localizedOptions, hideValues);

    const options: ComboBoxOption[] = [emptyOption, placeholderOption, ...localizedOptionsWithHidden];
    if (multiEditOptionText) {
        options.unshift(getMultiEditOption(multiEditOptionText));
    }
    return {
        children: children({ options }),
    };
};

/**
 * Wrapper zajišťuje konverzi typu enumu na lokalizované options.
 * Zároveň se stará o správné vytvoření defaultních options (multieditOption, placeholderOption, emptyOption) na základě jednotlivých příznaků.
 */
export const LocalizedComboBoxOptionsWrapper = connect(createMapStateToProps, undefined, mergeProps)(LocalizedComboBoxOptionsWrapperComponent);
